<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store.js';
import { useAlert } from 'dashboard/composables';

import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');

const canDelete = computed(() => currentUser.value?.role === 'administrator');

const priorityColor = computed(() => {
  switch (props.task.priority) {
    case 'urgent':
      return 'bg-n-ruby-9 text-white';
    case 'high':
      return 'bg-n-amber-9 text-white';
    case 'medium':
      return 'bg-n-blue-9 text-white';
    case 'low':
      return 'bg-n-slate-4 text-n-slate-12';
    default:
      return '';
  }
});

const dueDateLabel = computed(() => {
  if (!props.task.due_date) return null;
  const date = new Date(props.task.due_date);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
});

const isOverdue = computed(() => {
  if (!props.task.due_date) return false;
  return new Date(props.task.due_date).getTime() < Date.now();
});

const handleClick = () => {
  if (!props.task.conversation_id) return;
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: accountId.value,
      conversation_id: props.task.conversation_id,
    },
  });
};

const handleDelete = async event => {
  event.stopPropagation();
  if (!window.confirm(t('KANBAN.CARD.DELETE.CONFIRM'))) return;
  try {
    await store.dispatch('kanban/tasks/delete', {
      boardId: props.task.board_id,
      id: props.task.id,
    });
    useAlert(t('KANBAN.CARD.DELETE.SUCCESS'));
  } catch (error) {
    useAlert(t('KANBAN.CARD.DELETE.ERROR'));
  }
};
</script>

<template>
  <div
    class="group relative flex flex-col gap-2 p-3 bg-n-solid-1 border border-n-weak rounded-lg shadow-sm cursor-grab hover:border-n-strong transition-colors"
    @click="handleClick"
  >
    <button
      v-if="canDelete"
      type="button"
      :title="t('KANBAN.CARD.DELETE.TOOLTIP')"
      class="absolute top-1 right-1 inline-flex items-center justify-center size-6 rounded text-n-slate-10 opacity-0 group-hover:opacity-100 hover:bg-n-slate-3 hover:text-n-ruby-11 transition-opacity"
      @click="handleDelete"
    >
      <Icon icon="i-lucide-x" class="size-4" />
    </button>
    <h4 class="text-sm font-medium text-n-slate-12 line-clamp-2 pr-6">
      {{ task.title }}
    </h4>
    <p
      v-if="task.description"
      class="text-xs text-n-slate-11 line-clamp-2"
    >
      {{ task.description }}
    </p>
    <div
      v-if="task.conversation_labels?.length"
      class="flex items-center gap-1 flex-wrap"
    >
      <span
        v-for="label in task.conversation_labels"
        :key="label"
        class="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-n-slate-3 text-n-slate-12"
      >
        {{ label }}
      </span>
    </div>
    <div class="flex items-center gap-2 flex-wrap">
      <span
        v-if="task.priority"
        class="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase"
        :class="priorityColor"
      >
        {{ t(`KANBAN.PRIORITY.${task.priority.toUpperCase()}`) }}
      </span>
      <span
        v-if="dueDateLabel"
        class="inline-flex items-center gap-1 text-xs"
        :class="isOverdue ? 'text-n-ruby-11' : 'text-n-slate-11'"
      >
        <Icon icon="i-lucide-calendar" class="size-3" />
        {{ dueDateLabel }}
      </span>
    </div>
  </div>
</template>
